import { RoutineWorkflow, useEntityAlerts } from '@gripp/shared-logic';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dimensions,
  Modal,
  ScrollView,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';
import { Colors } from '../../../themes/colors';
import LoadingIndicator from '../../loadingIndicator';
import { TopModalBar } from '../../modals';
import { AlertSummary } from './alertSummary';
import { EditAlertModal } from './editAlertModal';

type EntityAlertListModalProps = {
  visible: boolean;
  onClose: () => void;
  entityType: 'group' | 'product' | 'asset';
  entityId: string;
};

export const EntityAlertListModal = ({
  visible,
  onClose,
  entityType,
  entityId,
}: EntityAlertListModalProps) => {
  const { t } = useTranslation();
  const [editAlertModalVisible, setEditAlertModalVisible] = useState(false);
  const [editAlertModalStyle, setEditAlertModalStyle] = useState<
    StyleProp<ViewStyle>
  >(styles.hidden);
  const [listModalStyle, setListModalStyle] = useState<StyleProp<ViewStyle>>(
    styles.outerContainer
  );
  const [selectedAlertId, setSelectedAlertId] = useState<string>('');
  const [selectedRoutineId, setSelectedRoutineId] = useState<string>('');

  const { workflows, isLoading, refetch, workspaceAssetId } = useEntityAlerts(
    entityType,
    entityId
  );

  const showEditAlertModal = () => {
    setListModalStyle(styles.hidden);
    setEditAlertModalVisible(true);
    setEditAlertModalStyle(styles.webModalBackground);
  };

  const hideEditAlertModal = () => {
    setEditAlertModalVisible(false);
    setEditAlertModalStyle(styles.hidden);
  };

  const closeEditAlertModal = () => {
    hideEditAlertModal();
    setListModalStyle(styles.outerContainer);
    setSelectedAlertId('');
    setSelectedRoutineId('');
    refetch();
  };

  const onAlertSelected = (alert: RoutineWorkflow) => {
    const alertId = alert?.id;
    const routineId = alert?.trigger?.config?.workspaceRoutineId;

    if (!alertId || !routineId) return;

    setSelectedAlertId(alertId);
    setSelectedRoutineId(routineId);
    showEditAlertModal();
  };

  const RenderAlerts = () => {
    return workflows?.map((item: RoutineWorkflow, index: number) => {
      const routineId = item.trigger?.config?.workspaceRoutineId || '';
      return (
        <AlertSummary
          onAlertSelected={onAlertSelected}
          key={`${index}-${item.id}`}
          workflow={item}
          workspaceAsset={null as any}
          routineId={routineId}
        />
      );
    });
  };

  return (
    <>
      <Modal visible={visible} transparent={true} onRequestClose={onClose}>
        <View style={listModalStyle}>
          <View style={styles.innerContainer}>
            <View style={styles.container}>
              <TopModalBar
                title={t('alerts.alert')}
                onCancel={onClose}
                backButtonLabel={t('topNav.back')}
              />
              {isLoading ? (
                <View
                  style={{
                    flex: 1,
                  }}
                >
                  <LoadingIndicator
                    backgroundColor={Colors.primaryGrayBackground}
                    flexContainer
                  />
                </View>
              ) : (
                <View style={styles.scrollViewContainer}>
                  <ScrollView>
                    <RenderAlerts />
                  </ScrollView>
                </View>
              )}
            </View>
          </View>
        </View>
      </Modal>

      {editAlertModalVisible &&
        selectedAlertId &&
        selectedRoutineId && (
          entityType !== 'asset' || workspaceAssetId ? (
            <EditAlertModal
              editAlertModalVisible={editAlertModalVisible}
              editAlertModalStyle={editAlertModalStyle}
              onClose={closeEditAlertModal}
              workspaceAssetId={entityType === 'asset' ? (workspaceAssetId ?? '') : ''}
              routineId={selectedRoutineId}
              alertId={selectedAlertId}
              productId={entityType === 'product' ? entityId : undefined}
              groupId={entityType === 'group' ? entityId : undefined}
            />
          ) : (
            <View style={editAlertModalStyle}>
              <LoadingIndicator
                backgroundColor={Colors.primaryGrayBackground}
                flexContainer
              />
            </View>
          )
        )}
    </>
  );
};

const styles = StyleSheet.create({
  outerContainer: {
    flex: 1,
    backgroundColor: Colors.blackInactive,
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    justifyContent: 'center',
    alignItems: 'center',
  },
  hidden: {
    display: 'none',
  },
  webModalBackground: {
    flex: 1,
    backgroundColor: Colors.blackInactive,
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    justifyContent: 'center',
    alignItems: 'center',
  },
  innerContainer: {
    width: '100%',
    maxWidth: 560,
    minHeight: 461,
    maxHeight: Dimensions.get('window').height * 0.8,
    borderRadius: 12,
    backgroundColor: Colors.primaryGrayBackground,
    elevation: 10,
    overflow: 'hidden',
  },
  container: {
    flex: 1,
    backgroundColor: Colors.primaryGrayBackground,
  },
  scrollViewContainer: {
    flex: 1,
    margin: 20,
  },
});
